import { i18n } from './../../../i18n';
import { BidPhase, BidStrategyType, CampaignFormData, EcommType, SmartBidType } from '@tt4b/ttam-enums';
import { Ref } from '@vue/composition-api';
import { TranslateResult } from 'vue-i18n';

export interface StrategyOption {
  type: BidStrategyType;
  /** 视图上的类型，用于升级后视图展示，实际类型是 type*/
  viewType: BidStrategyType;
  value: SmartBidType;
  /** 出价策略提示，unified 后的新文案 */
  hint: string;
  optionalLabelTip?: TranslateResult;
  optionalForClickLabelTip?: TranslateResult;
  requireLabelTip?: TranslateResult;
  requireForClickLabelTip?: TranslateResult;
  title: TranslateResult;
  titleDesc?: TranslateResult;
  hasBidSet: boolean;
  bidSetKey?: string;
  dataKey?: string;
  bidPhase?: BidPhase;
  showSuggestPrice?: boolean;
  isBeta?: boolean;
  toolTitle: TranslateResult;
  bestFor: TranslateResult;
  thingsToConsider: TranslateResult;
}

/**
 * 所有的 Strategy 项以及对应的配置
 * 排序规则：cpx 时候默认 ，LOWEST_COST，所以 LOWEST_COST 优先；ocpx 时候默认 COST_CAP，所以 COST_CAP 优先
 */
export const allStrategies = (
  campaignData: Ref<CampaignFormData>,
  isBidCapToCostCapTip?: boolean,
): StrategyOption[] => {
  const isClosedLoop = campaignData.value.ecommType === EcommType.SHOP;
  const list = [
    {
      type: BidStrategyType.LOWEST_COST,
      viewType: BidStrategyType.LOWEST_COST,
      value: SmartBidType.NO_BID,
      hint: 'module_unified_bidding_sub_title_maximize_delivery',
      title: i18n.t('module_bid_strategy_lowest_cost_new'),
      titleDesc: i18n.t('module_bid_strategy_lowestcost_desc'),
      hasBidSet: false,
      toolTitle: i18n.t('module_bid_control_cpc_off_tooltip_title_new'),
      bestFor: i18n.t('module_bid_strategy_tooltip_lowestcost_best_for'),
      thingsToConsider: i18n.t('module_bid_strategy_tooltip_lowestcost_things_to_consider'),
    },
    {
      type: BidStrategyType.COST_CAP,
      viewType: BidStrategyType.COST_CAP,
      value: SmartBidType.STANDARD,
      hint: 'module_unified_bidding_sub_title_cost_cap',
      title: i18n.t('module_bid_strategy_costcap'),
      titleDesc: i18n.t('module_bid_strategy_costcap_desc'),
      hasBidSet: true,
      optionalLabelTip: i18n.t('module_unified_bidding_title_cost_cap_hover_tips_optional'),
      optionalForClickLabelTip: i18n.t('module_unified_bidding_title_cost_cap_click_hover_tips_optional'),
      requireLabelTip: i18n.t('module_unified_bidding_title_cost_cap_hover_tips'),
      requireForClickLabelTip: i18n.t('module_unified_bidding_title_cost_cap_click_hover_tips'),
      bidSetKey: 'CostCap',
      dataKey: 'cpaBid',
      bidPhase: BidPhase.CPA_BID,
      showSuggestPrice: true,
      toolTitle: i18n.t('module_bid_control_ocpc_on_tooltip_title'),
      bestFor: i18n.t('module_bid_strategy_tooltip_costcap_best_for'),
      thingsToConsider: i18n.t('module_bid_strategy_tooltip_costcap_things_to_consider'),
    },
    {
      type: BidStrategyType.BID_CAP,
      viewType: BidStrategyType.BID_CAP,
      value: SmartBidType.STANDARD,
      hint: 'module_unified_bidding_sub_title_bid_cap',
      title: i18n.t('module_bid_strategy_bid_cap'),
      titleDesc: i18n.t('module_bid_strategy_bidcap_desc'),
      hasBidSet: true,
      optionalLabelTip: i18n.t('module_unified_bidding_title_bid_cap_hover_tips_optional'),
      optionalForClickLabelTip: i18n.t('module_unified_bidding_title_bid_cap_click_hover_tips_optional'),
      requireLabelTip: i18n.t('module_unified_bidding_title_cost_cap_hover_tips'),
      requireForClickLabelTip: i18n.t('module_unified_bidding_title_bid_cap_click_hover_tips'),
      bidSetKey: 'BidCap',
      dataKey: 'bid',
      bidPhase: BidPhase.BID,
      showSuggestPrice: false,
      toolTitle: i18n.t('module_bid_control_cpc_on_tooltip_title'),
      bestFor: i18n.t('module_bid_strategy_tooltip_bidcap_best_for'),
      thingsToConsider: i18n.t('module_bid_strategy_tooltip_bidcap_things_to_consider'),
    },
    {
      type: BidStrategyType.MAXCV,
      viewType: BidStrategyType.MAXCV,
      value: SmartBidType.MAX_CONVERTION,
      hint: '',
      title: i18n.t('module_bid_strategy_maxcv'),
      titleDesc: i18n.t('module_bid_strategy_maxcv_withbid_desc'),
      hasBidSet: true,
      bidSetKey: 'MaxCV',
      dataKey: 'cpaBid',
      bidPhase: BidPhase.CPA_BID,
      showSuggestPrice: true,
      isBeta: true,
      toolTitle: i18n.t('module_bid_strategy_maxcv_abbr'),
      bestFor: i18n.t('module_bid_strategy_tooltip_maxcv_best_for'),
      thingsToConsider: i18n.t('module_bid_strategy_tooltip_maxcv_things_to_consider'),
    },
    {
      type: BidStrategyType.HIGHEST_VALUE,
      viewType: BidStrategyType.HIGHEST_VALUE,
      value: SmartBidType.NO_BID,
      hint: isClosedLoop
        ? 'module_unified_bidding_sub_title_hightest_value_shop'
        : 'module_unified_bidding_sub_title_hightest_value',
      title: isClosedLoop
        ? i18n.t('module_bid_strategy_highest_value_title_v2')
        : i18n.t('module_bid_strategy_highest_value_title'),
      titleDesc: i18n.t('module_bid_strategy_highest_value_desc'),
      hasBidSet: true,
      bidSetKey: 'HighestValue',
      dataKey: 'roasBid',
      bidPhase: BidPhase.CPA_BID,
      showSuggestPrice: true,
      toolTitle: isClosedLoop
        ? i18n.t('module_bid_strategy_highest_value_title_v2')
        : i18n.t('module_bid_strategy_highest_value_title'),
      bestFor: i18n.t('module_bid_strategy_tooltip_highest_value_best_for'),
      thingsToConsider: i18n.t('module_bid_strategy_tooltip_highest_value_things_to_consider'),
    },
    {
      type: BidStrategyType.MIN_ROAS,
      viewType: BidStrategyType.MIN_ROAS,
      value: SmartBidType.NO_BID,
      hint: isClosedLoop
        ? 'module_unified_bidding_sub_title_min_roas_shop'
        : 'module_unified_bidding_sub_title_min_roas',
      title: i18n.t('module_bid_strategy_roas_title'),
      toolTitle: i18n.t('module_bid_strategy_roas_title'),
      dataKey: 'roasBid',
      hasBidSet: true,
      optionalLabelTip: isClosedLoop
        ? i18n.t('module_unified_bidding_title_min_roas_hover_tips_optional_shop')
        : i18n.t('module_unified_bidding_title_min_roas_hover_tips_optional'),
      requireLabelTip: i18n.t('module_unified_bidding_title_min_roas_hover_tips'),
      bestFor: i18n.t('module_bid_strategy_tooltip_roas_best_for'),
      thingsToConsider: i18n.t('module_bid_strategy_tooltip_roas_things_to_consider'),
    },
  ];

  /** bid cap 升级到 cost cap 则将文案展示为 cost cap */
  if (isBidCapToCostCapTip) {
    const bidCap = list.find(item => item.type === BidStrategyType.BID_CAP);
    const costCap = list.find(item => item.type === BidStrategyType.COST_CAP);
    const {
      // 视图上的类型用于获取图片等
      viewType,
      // unified bid strategy
      hint,
      optionalLabelTip,
      optionalForClickLabelTip,
      requireLabelTip,
      requireForClickLabelTip,
      // old ui
      title,
      titleDesc,
      // view detail
      toolTitle,
      bestFor,
      thingsToConsider,
    } = costCap;

    Object.assign(bidCap, {
      viewType,
      hint,
      optionalLabelTip,
      optionalForClickLabelTip,
      requireLabelTip,
      requireForClickLabelTip,
      title,
      titleDesc,
      toolTitle,
      bestFor,
      thingsToConsider,
    });
  }

  return list;
};
